import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(data);
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <div className="bg-white p-12 rounded-3xl shadow-lg">
            <Loader2 className="w-16 h-16 text-brand-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-2xl font-semibold text-brand-dark mb-2">
              Processing Payment...
            </h1>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment.
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white p-12 rounded-3xl shadow-lg">
            <div className="w-20 h-20 bg-brand-mist rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-brand-primary" />
            </div>
            <h1 className="font-serif text-3xl font-semibold text-brand-dark mb-4">
              Payment Successful!
            </h1>
            <p className="text-muted-foreground mb-6">
              Thank you for your purchase. You will receive a confirmation email shortly.
            </p>
            
            {paymentDetails && (
              <div className="bg-brand-cream rounded-2xl p-6 mb-8 text-left">
                <h3 className="font-semibold text-brand-dark mb-3">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Package:</span>
                    <span className="font-medium text-brand-dark">{paymentDetails.package_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-medium text-brand-dark">
                      ${paymentDetails.amount?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Link
                to="/contact"
                onClick={() => window.scrollTo(0, 0)}
                data-testid="success-contact-btn"
                className="block w-full bg-brand-primary hover:bg-brand-medium text-white rounded-full px-6 py-3 font-medium transition-all"
              >
                Book Your First Session
              </Link>
              <Link
                to="/"
                className="block w-full text-brand-primary hover:text-brand-medium font-medium transition-colors"
              >
                Return to Home
              </Link>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="bg-white p-12 rounded-3xl shadow-lg">
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="font-serif text-3xl font-semibold text-brand-dark mb-4">
              {status === 'timeout' ? 'Payment Verification Timeout' : 'Payment Issue'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {status === 'timeout' 
                ? 'We couldn\'t verify your payment status. Please check your email for confirmation or contact us.'
                : status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'There was an issue processing your payment. Please try again or contact us for help.'}
            </p>
            <div className="space-y-3">
              <Link
                to="/packages"
                data-testid="error-retry-btn"
                className="block w-full bg-brand-primary hover:bg-brand-medium text-white rounded-full px-6 py-3 font-medium transition-all"
              >
                Try Again
              </Link>
              <Link
                to="/contact"
                onClick={() => window.scrollTo(0, 0)}
                className="block w-full text-brand-primary hover:text-brand-medium font-medium transition-colors"
              >
                Contact Support
              </Link>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
